import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import mergeOutstandingDepositsWithClients from 'bl/deposits/mergeOutstandingDepositsWithClients.bl';
import sortOutstandingDeposits from 'bl/deposits/sortOutstandingDeposits.bl';
import determineErrorMessage from 'bl/response/determineErrorMessage.bl';
import { ModalContext } from 'components/core/ModalRoot.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import FLAG from 'config/flag.singleton';
import DELAY from 'constants/delay.const';
import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useThemeWithBright } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import delayed from 'utils/functions/delayed.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import fixed from 'utils/values/fixed.util';

import PopupModalContainer from 'common/PopupModal';
import TopNav from 'common/TopNav.component';
import { getClientsList } from 'salon/actions';
import { createRefundAndRefresh, fetchClientDepositDetails, fetchDepositClientsList } from 'settings/balance-sheet/actions';
import BalanceSheetLayoutDetails from 'settings/balance-sheet/BalanceSheetLayoutDetails';
import BalanceSheetLayoutSummary from 'settings/balance-sheet/BalanceSheetLayoutSummary.component';
import DepositRefundOnlineWizard from 'settings/balance-sheet/DepositRefundOnlineWizard';
import Search from 'settings/balance-sheet/DepositSearch';
import { LockedRefundDialog } from 'settings/balance-sheet/LockedRefundDialog.component';

import CN from './BalanceSheetPage.module.scss';

const CTAG = 'BalanceSheetPage';
const L = logger(CTAG);

const USE_INVALID_AMOUNT = true;
const USE_SPAM_LOG = fromAnyToBool(FLAG.loggerSpam);

const byClientName = (name) => {
  const lowered = fromAnyToLowerCase(name);
  return (client) => fromAnyToLowerCase(client?.clientName).includes(lowered);
};

const sumDepositsByField = (array, field) => fixed(asArray(array).reduce((sum, e) => sum + e?.[field], 0));

const ScrollToHandle = React.forwardRef(({ tag, subtag, style }, ref) => (
  <div data-bem="ScrollHandle" data-tag={fromAnyToTrimmed(tag)} data-subtag={fromAnyToTrimmed(subtag)} style={style} ref={ref} />
));

const Spinner = ({ message }) => (
  <div className="search-loading-container">
    <div className="spinner-container">
      <div className="load-spinner" />
      <span>{message}</span>
    </div>
  </div>
);

const BalanceSheetPage = (props) => {
  const nav = navFor(CTAG, props);
  const { createRefundAndRefresh, fetchClientDepositDetails, fetchDepositClientsList, getClientsList } = props;

  const salonId = useCurrentSalonId();
  const { t } = useTranslation();
  const { isDarkMode, isLightMode, isBrightMode } = useThemeWithBright();

  const { val: isAlreadyRendered, yay: markAlreadyRendered } = useTip(false);
  const { val: isOnlineWizardRendered, yay: openOnlineWizard, nay: closeOnlineWizard } = useTip(false);

  // TODO: instead of {state: { clientId }} use search params (e.g. ?clientId=)
  const [openedId, setOpenedId] = useState(props?.location?.state?.clientId);
  const [scrollToElement, setScrollToElement] = useStateWhileMounted(null);

  const [calledOpenId, setCalledOpenId] = useState(0);
  const [search, setSearch] = useState(S.empty);

  const [source, setSource] = useState();
  const [amount, setAmount] = useState();
  const [available, setAvailable] = useState();

  const [errorMessage, setErrorMessage] = useState('');
  const hideError = useCallback(() => void setErrorMessage(S.empty), []);

  // This modal should appear if the user tries to refund an amount which is fully or partially "locked" to a future appointment
  const refundOffline = useCallback(
    (ev) =>
      void ev.showModal(({ onRequestClose }) => (
        <LockedRefundDialog
          value={ev?.parsedAmount}
          source={ev?.source}
          onClose={onRequestClose}
          onConfirm={() => {
            L.unignore('refundOffline()', 'createRefundAndRefresh:', createRefundAndRefresh(salonId, ev?.clientId, ev?.parsedAmount, ev?.source)).then(() => {
              // TODO: use linkTo here
              nav.push({ pathname: `/salon/${salonId}/deposits/send_receipt` });
            });
          }}
        />
      )),
    [createRefundAndRefresh, nav, salonId],
  );

  const [{ value: valueClients, loading: isLoadingClients }, loadClients] = useAsyncFn(async () => {
    L.info('Loading clients...');

    const result = await getClientsList(salonId);

    const em = determineErrorMessage(result);
    if (em) setErrorMessage(L.warn('useEffect()', 'errorClients:', em));

    const json = result?.json;
    if (USE_SPAM_LOG) L.debug('useEffect()', 'valueClients:', json);
    return json;
  }, [getClientsList, salonId]);

  const [{ value: valueDeposits, loading: isLoadingDeposits }, loadDeposits] = useAsyncFn(async () => {
    L.info('Loading deposits...');

    const result = await fetchDepositClientsList(salonId);

    const em = determineErrorMessage(result);
    if (em) setErrorMessage(L.warn('loadDeposits()', 'em:', em));

    const json = result?.json;
    if (USE_SPAM_LOG) L.debug('loadDeposits()', 'json:', json);
    return json;
  }, [fetchDepositClientsList, salonId]);

  const [{ loading: isLoadingDetails, value: valueDetails }, loadDetails] = useAsyncFn(
    async (options) => {
      const clientId = options?.clientId;
      if (!clientId) return void 1;

      L.info('Loading details...');

      const result = await fetchClientDepositDetails(salonId, clientId);

      const em = determineErrorMessage(result);
      if (em) setErrorMessage(L.warn('loadDetails()', 'em:', em));

      const json = result?.json;
      if (USE_SPAM_LOG) L.debug('loadDetails()', 'json:', json);
      return json;
    },
    [fetchClientDepositDetails, salonId],
  );

  const load = useCallback(() => {
    // noinspection JSIgnoredPromiseFromCall
    loadClients();
    // noinspection JSIgnoredPromiseFromCall
    loadDeposits();
    // noinspection JSIgnoredPromiseFromCall
    loadDetails({ clientId: openedId });
  }, [loadClients, loadDeposits, loadDetails, openedId]);

  const handleSearch = useCallback((ev) => void setSearch(fromAnyToLowerCase(fromAnyToTrimmed(fromEventToValue(ev)))), []);

  const handleCloseOnlineRefundWizard = useCallback(
    (ev) => {
      L.debug('handleCloseOnlineRefundWizard()', ev);
      closeOnlineWizard();
      if (ev?.refundedAmount) {
        load();
      }
    },
    [closeOnlineWizard, load],
  );

  const selectDetails = useCallback(
    (ev) => {
      const isTheSameId = openedId === ev?.clientId;
      const clientId = ev?.clientId ?? 0;
      setOpenedId(isTheSameId ? 0 : clientId);
      if (!isTheSameId) {
        // noinspection JSIgnoredPromiseFromCall
        loadDetails({ clientId });
      }
    },
    [loadDetails, openedId],
  );

  useEffect(() => {
    if (isAlreadyRendered) return;
    load();
    markAlreadyRendered();
  }, [isAlreadyRendered, load, markAlreadyRendered, openedId, valueClients, valueDeposits]);

  useEffect(() => {
    if (!scrollToElement) return;
    delayed(DELAY.fast, () => void elementScrollTo({ element: scrollToElement, block: 'start' }));
  }, [scrollToElement]);

  const deposits = mergeOutstandingDepositsWithClients({ clients: valueClients, deposits: valueDeposits });
  const filtered = search.length < 2 ? deposits : deposits.filter(byClientName(search));
  const isLoading = fromAnyToBool(isLoadingClients || isLoadingDeposits || isLoadingDetails);

  if (USE_SPAM_LOG) L.debug('()', { amount, source }, { isLoading, valueClients, valueDeposits, valueDetails });

  return (
    <ModalContext.Consumer>
      {({ showModal }) => (
        <div
          data-bem="BalanceSheetPage"
          className={cn({
            'salon settings': true,
            'light-class': isLightMode,
            'dark-class': isDarkMode,
            'bright-class': isBrightMode,
          })}
        >
          {fromAnyToBool(errorMessage) && <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorMessage} buttonText={t('common.buttons.okay')} buttonCallback={hideError} />}

          <TopNav title={t('balanceSheet.title')}>
            <SidebarMenu />
          </TopNav>

          <div data-bem="BalanceSheetPage__body" className="online-booking online-booking-layout">
            {deposits ? (
              <Search
                totalCash={sumDepositsByField(deposits, DEPOSIT_SOURCE.cash)}
                totalCard={sumDepositsByField(deposits, DEPOSIT_SOURCE.card)}
                totalPay={sumDepositsByField(deposits, DEPOSIT_SOURCE.online)}
                otherDeposit={sumDepositsByField(deposits, DEPOSIT_SOURCE.other)}
                totalSlickPos={sumDepositsByField(deposits, DEPOSIT_SOURCE.pos)}
                totalDeposit={sumDepositsByField(deposits, 'total')}
                onChange={handleSearch}
              />
            ) : (
              <Spinner message={t('balanceSheet.loadingMsg')} />
            )}

            {isLoading ? (
              <Spinner message={t('balanceSheet.loadingMsg')} />
            ) : isFullArray(filtered) ? (
              <div data-bem="BalanceSheetPage__balance-container" className="balance-container">
                <div className="sub-title sticky-nav">
                  <div>
                    <span>{t('balanceSheet.tableHeaders.outstandingDeposits')}</span>
                  </div>
                  <div>
                    <span>{t('balanceSheet.tableHeaders.cash')}</span>
                    <span>{t('balanceSheet.tableHeaders.balance')}</span>
                  </div>
                  <div>
                    <span>{t('balanceSheet.tableHeaders.cardInStore')}</span>
                    <span>{t('balanceSheet.tableHeaders.nonSlick')}</span>
                    <span>{t('balanceSheet.tableHeaders.balance')}</span>
                  </div>
                  <div>
                    <span>{t('balanceSheet.tableHeaders.cardInStore')}</span>
                    <span>{t('balanceSheet.tableHeaders.slickPay')}</span>
                    <span>{t('balanceSheet.tableHeaders.balance')}</span>
                  </div>
                  <div>
                    <span>{t('balanceSheet.tableHeaders.cardOnline')}</span>
                    <span>{t('balanceSheet.tableHeaders.slickPay')}</span>
                    <span>{t('balanceSheet.tableHeaders.balance')}</span>
                  </div>
                  <div>
                    <span>{t('common.other')}</span>
                    <span>{t('balanceSheet.tableHeaders.balance')}</span>
                  </div>
                  <div className="text-bold">
                    <span>{t('common.total')}</span>
                    <span>{t('balanceSheet.tableHeaders.balance')}</span>
                  </div>
                  <div />
                </div>

                {sortOutstandingDeposits(filtered).map((outstanding) => (
                  <div data-bem="BalanceSheetPage__balance" key={outstanding?.clientId} className="box">
                    <ScrollToHandle
                      tag="BalanceSheetPage__scroll-handle"
                      style={{ top: '-120px', position: 'relative' }}
                      ref={(element) => {
                        if (openedId === outstanding?.clientId && !isLoading) setScrollToElement(element);
                      }}
                    />
                    <div className="scroll">
                      <BalanceSheetLayoutSummary open={openedId === outstanding?.clientId} openedId={openedId} value={outstanding} onToggle={() => void selectDetails(outstanding)} />

                      {openedId === outstanding?.clientId &&
                        (isLoading ? (
                          <Spinner />
                        ) : (
                          <BalanceSheetLayoutDetails
                            start={calledOpenId}
                            openedId={openedId}
                            salonId={salonId}
                            outstanding={outstanding}
                            clientName={outstanding.clientName}
                            onStart={(ev) => setCalledOpenId(ev?.openedId)}
                            onRefund={(ev) => {
                              L.debug('handleRefund()', ev?.isValidAmount, ev);

                              setSource(ev?.source);
                              setAmount(ev?.parsedAmount);
                              setAvailable(ev?.available);

                              if (ev?.source === DEPOSIT_SOURCE.online || ev?.source === DEPOSIT_SOURCE.pos) {
                                openOnlineWizard();
                              } else if (USE_INVALID_AMOUNT || !ev?.isInvalid) {
                                refundOffline({
                                  salonId,
                                  clientId: outstanding?.clientId,
                                  parsedAmount: ev?.parsedAmount,
                                  source: ev?.source,
                                  showModal,
                                });
                              }
                            }}
                          />
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={CN.noResults}>{t('balanceSheet.clientNotFound')}</div>
            )}

            {isOnlineWizardRendered && <DepositRefundOnlineWizard clientId={openedId} source={source} amount={amount} available={available} onClose={handleCloseOnlineRefundWizard} />}
          </div>
        </div>
      )}
    </ModalContext.Consumer>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchDepositClientsList,
      fetchClientDepositDetails,
      createRefundAndRefresh,
      getClientsList,
    },
    dispatch,
  );

const BalanceSheetPageHoc = connect(null, mapDispatchToProps)(BalanceSheetPage);

export default BalanceSheetPageHoc;
